import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProductCategory } from '../types/product';
import { CATEGORIES } from '../types/product';
import { useProducts } from '../hooks/useProducts';
import { ProductFilter, ProductGrid } from '../components/products';

const ProductsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { products, isLoading } = useProducts();
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | null>(
    (searchParams.get('category') as ProductCategory) || null
  );
  const [sortBy, setSortBy] = useState('featured');
  const [organicOnly, setOrganicOnly] = useState(false);

  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query) ||
          p.category.toLowerCase().includes(query)
      );
    }

    // Filter by category
    if (selectedCategory) {
      result = result.filter((p) => p.category === selectedCategory);
    }

    // Filter by organic
    if (organicOnly) {
      result = result.filter((p) => p.organic);
    }

    // Sort
    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'newest':
        result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      default:
        // Featured - sort by featured flag and rating
        result.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || b.rating - a.rating);
    }

    return result;
  }, [products, searchQuery, selectedCategory, sortBy, organicOnly]);

  const handleCategoryChange = (category: ProductCategory | null) => {
    setSelectedCategory(category);
    const params = new URLSearchParams(searchParams);
    if (category) {
      params.set('category', category);
    } else {
      params.delete('category');
    }
    setSearchParams(params);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategory(null);
    setSortBy('featured');
    setOrganicOnly(false);
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-dark-dark dark:bg-gray-50 transition-colors duration-300">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-accent via-accent-light to-primary py-12 md:py-16 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3 md:mb-4">
            Fresh Farm Products
          </h1>
          <p className="text-base md:text-lg lg:text-xl text-white/90 max-w-2xl mx-auto px-4">
            Discover our wide selection of organic, fresh agricultural products 
            delivered directly from local farms
          </p>
        </div>
      </div>

      {/* Products Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        {/* Filter */}
        <ProductFilter
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          sortBy={sortBy}
          onSortChange={setSortBy}
          organicOnly={organicOnly}
          onOrganicChange={setOrganicOnly}
          onClearFilters={handleClearFilters}
        />

        {/* Results Count */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <p className="text-gray-300 dark:text-gray-600 text-sm md:text-base transition-colors duration-300">
            Showing <span className="font-semibold">{filteredProducts.length}</span> products
          </p>
          {selectedCategory && (
            <button
              onClick={() => {
                const category = CATEGORIES.find((c) => c.id === selectedCategory);
                if (category) {
                  <span className="text-sm text-accent dark:text-primary font-medium transition-colors duration-300">
                    Category: {category.name}
                  </span>;
                }
              }}
              className="text-sm text-gray-400 dark:text-gray-500 hover:text-accent dark:hover:text-primary transition-colors duration-300"
            >
            </button>
          )}
        </div>

        {/* Product Grid */}
        <ProductGrid
          products={filteredProducts}
          isLoading={isLoading}
          emptyMessage={
            searchQuery || selectedCategory
              ? 'No products match your filters'
              : 'No products available'
          }
        />
      </div>
    </div>
  );
};

export default ProductsPage;